import moment from "moment-timezone";
import changes from "./changes.json";


export const getQueuesAPI = (url, body, callback, token) => {
  fetch("https://api.queuesapp.com/" + url, {
    headers: { "Content-Type": "application/json", token: token },
    method: "POST",
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok && url !== "send-dashboard-password-reset-code") {
        //throw new Error(`HTTP error! status: ${response.status}`);
        throw response;
      }
      return response.json();
    })
    .then((data) => callback(data))
    .catch((error) => {
      // Do not have this error line active in PROD! Shows sensitive information
      // console.error("There was a problem with the fetch operation:", error);
      callback(null, error);
    });
};

export const getChanges = (lastLoggedIn) => {
  let allChanges = require("./changes.json");
  return allChanges;
};

// Commented out for now since we are passing location from OccCard to location.js
// export const getData = (id) => {
//   return require("./vendors/braves/locations.json").locations.filter(
//     (x) => x.id === id
//   )[0];
// };


export const formatTimestamp = (timestamp) => {
  let year = timestamp.getFullYear();
  let month = ("0" + (timestamp.getMonth() + 1)).slice(-2);
  let day = ("0" + timestamp.getDate()).slice(-2);
  let hours = ("0" + timestamp.getHours()).slice(-2);
  let minutes = ("0" + timestamp.getMinutes()).slice(-2);

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const generateDay = (timestamp) => {
  let day = [];
  // was x < 1440 for midnight but we now want to get data for a day until 3:00am
  for (let x = 0; x < 1620; x++) {
    day.push(
      formatTimestamp(
        moment(timestamp).startOf("day").add(x, "minutes").toDate()
      )
    );
  }
  return day;
};

// Helper function to round minutes to the next hour
export const roundToNextHour = (minutes) => {
  const currentHour = Math.floor(minutes / 60);
  const nextHour = currentHour + 1;
  return nextHour * 60;
};

// Intelligent cutoff function to find when significant activity ends
// Used in average calculations on map page for retail store
// Used in heatmap calculations on charts page for retail store
export const findIntelligentCutoff = (data, startTime) => {
  const timeKeys = Object.keys(data)
    .map(key => parseInt(key, 10))
    .sort((a, b) => a - b);
  
  const activeTimeKeys = timeKeys.filter(time => time >= startTime);
  
  if (activeTimeKeys.length < 10) {
    return Math.max(...activeTimeKeys);
  }
  
  const occupancyByTime = activeTimeKeys.map(timeKey => ({
    time: timeKey,
    total: data[timeKey]?.total || 0
  }));
  
  // Dynamic threshold: 10% of peak, minimum 15
  const maxOccupancy = Math.max(...occupancyByTime.map(p => p.total));
  const significantThreshold = Math.max(15, Math.round(maxOccupancy * 0.1));
  
  const SMOOTHING_WINDOW = 5;
  const SUSTAINED_DROP_LENGTH = 15;
  
  // Smooth the data
  const smoothed = occupancyByTime.map((point, i) => {
    let sum = 0;
    let count = 0;
    const halfWindow = Math.floor(SMOOTHING_WINDOW / 2);
    for (let j = Math.max(0, i - halfWindow); j <= Math.min(occupancyByTime.length - 1, i + halfWindow); j++) {
      sum += occupancyByTime[j].total;
      count++;
    }
    return {
      time: point.time,
      smoothedTotal: Math.round(sum / count)
    };
  });
  
  // Simple forward scan:
  // Track the last time we saw significant activity.
  // That's your cutoff — it naturally updates as the day progresses.
  let lastActiveTime = startTime;
  let inLull = false;
  let lullStart = 0;
  
  for (let i = 0; i < smoothed.length; i++) {
    if (smoothed[i].smoothedTotal >= significantThreshold) {
      // Activity — update the cutoff
      lastActiveTime = smoothed[i].time;
      inLull = false;
    } else {
      if (!inLull) {
        lullStart = i;
        inLull = true;
      }
    }
  }
  
  // Check: did the day end in a sustained lull?
  // If not, the venue is still active — cutoff is the last data point
  const lullLength = inLull ? smoothed.length - lullStart : 0;
  
  if (lullLength >= SUSTAINED_DROP_LENGTH) {
    // Ended in a real lull — use the last active point
    const timeStr = `${Math.floor(lastActiveTime/60)}:${String(lastActiveTime%60).padStart(2,'0')}`;
    // console.log(`findIntelligentCutoff: last activity at ${timeStr}, lull of ${lullLength} mins at end`);
    return lastActiveTime;
  }
  
  // Still active or short lull — use the last available data point
  const lastTime = smoothed[smoothed.length - 1].time;
  const timeStr = `${Math.floor(lastTime/60)}:${String(lastTime%60).padStart(2,'0')}`;
  // console.log(`findIntelligentCutoff: still active (lull only ${lullLength} mins), using end: ${timeStr}`);
  return lastTime;
};

const TEAM_FILES = {
  mlb: () => require("./clients/mlb_teams.json"),
  nfl: () => require("./clients/nfl_teams.json"),
  ncaaf: () => require("./clients/ncaaf_teams.json"),
};

// This is used for selecting the team the user is apart of given a venue
// Main usage in account view and main app header
export const getUserTeam = (league, venue) => {
  const teams = TEAM_FILES[league]?.();
  return teams?.find((team) => team.venue?.toLowerCase() === venue.toLowerCase());
};

// This is used for selecting a team given league and teamID
// mainly used in Overview page and Game selector
export const getTeam = (league, teamId) => {
  const teams = TEAM_FILES[league]?.();
  return teams?.find((x) => x.tid === teamId);
};

// Adds additional hard coded locations for testing
// Originally from locations.json file (deprecated)
// Make sure to add collection value
export const addAdditionalLocations = (authLocationData) => {
  // Note a maxOccupancy of 0 designates the location as a door counting location only
  const additionalData = {
    "retail": [
      // {
      //   "id": 2,
      //   "collection": "threads",
      //   "measurement": "clubhouse",
      //   "doorMeasurement": "clubhouseDoor",
      //   "league": "mlb",
      //   "name": "Threads",
      //   "mapId": "ClubhouseMap",
      //   "logoNameMobile": "clubhouse_logo",
      //   "logoPath": "mlb/braves/stadium_stores/threads_logo.png",
      //   "logoPathDark": "mlb/braves/stadium_stores/threads_logo.png",
      //   "maxOccupancy": 0,
      //   "totalOccupancySensors": 0,
      //   "downedSensorBuffer": 0,
      //   "doorCounterSensorIds": [0,1],
      //   "calendarMinDate": "2024-08-15",
      //   "doorsOpenTime": [720, 660, 660, 660, 660, 660, 660], 
      //   "timeZone": "America/New_York",
      //   "gameEndCutoff": 1285,
      //   "doorsOpenBuffer": 30,
      //   "gameEndBuffer": 210,
      //   "defaultZoneLabel": "Merch",
      //   "sortOptions": [
          
      //   ],
      //   "dayTypes": {
      //     "GAME": "game",
      //     "REGULAR": "regular"
      //   },
      //   "sectionTypes": {
      //     "COUNTER": "Counter",
      //   },
      //   "zones": []
      // },
      // {
      //   "id": 3,
      //   "collection": "clubhouse",
      //   "measurement": "clubhouse",
      //   "doorMeasurement": "clubhouseDoor",
      //   "league": "mlb",
      //   "name": "Braves Authentics",
      //   "mapId": "ClubhouseMap",
      //   "logoNameMobile": "clubhouse_logo",
      //   "logoPath": "mlb/braves/stadium_stores/braves_authentics_logo.png",
      //   "logoPathDark": "mlb/braves/stadium_stores/braves_authentics_logo.png",
      //   "maxOccupancy": 0,
      //   "totalOccupancySensors": 0,
      //   "downedSensorBuffer": 0,
      //   "doorCounterSensorIds": [0,1],
      //   "calendarMinDate": "2024-08-15",
      //   "doorsOpenTime": [720, 660, 660, 660, 660, 660, 660], 
      //   "timeZone": "America/New_York",
      //   "gameEndCutoff": 1285,
      //   "doorsOpenBuffer": 30,
      //   "gameEndBuffer": 210,
      //   "defaultZoneLabel": "Merch",
      //   "sortOptions": [
          
      //   ],
      //   "dayTypes": {
      //     "GAME": "game",
      //     "REGULAR": "regular"
      //   },
      //   "sectionTypes": {
      //     "COUNTER": "Counter",
      //     "CHARTS": "Charts",
      //   },
      //   "zones": []
      // }
    ],
    "concessions": [
      {
        "id": 2,
        "collection": "ballparkClassics",
        "measurement": "ballparkClassics",
        "name": "Ballpark Classics",
        "logoPath": "mlb/braves/concession_logos/ballpark_classics.png",
        // "sections": [113, 141, 215, 239, 331],
        // "waitTimes": [12, 8, 23, 2, 26],
        "type": "food",
        "sections" : [
          {
            "sectionId": 116,
            "label": "Lower Deck"
          },
          {
            "sectionId": 135,
            "label": "Lower Deck"
          },
          {
            "sectionId": 136,
            "label": "Lower Deck"
          },
          {
            "sectionId": 147,
            "label": "Lower Deck"
          },
          {
            "sectionId": 242,
            "label": "Upper Deck"
          },
        ]
      },
      // {
      //   "name": "Ballpark Classics",
      //   "logoPath": "mlb/braves/concession_logos/ballpark_classics.png",
      //   "sections": [116, 135, 146, 242],
      //   "waitTimes": [7, 9, 14, 12],
      //   "type": "food"
      // },
      // {
      //   "name": "Braves Grab & Go",
      //   "logoPath": "mlb/braves/concession_logos/grab_go.png",
      //   "sections": [133],
      //   "waitTimes": [9],
      //   "type": "food"
      // },
      // {
      //   "name": "Braves Grill & Go",
      //   "logoPath": "mlb/braves/concession_logos/grill_go.png",
      //   "sections": [137, 317, 337],
      //   "waitTimes": [2, 14, 19],
      //   "type": "food"
      // },
      // {
      //   "name": "Braves Market",
      //   "logoPath": "mlb/braves/concession_logos/market.png",
      //   "sections": [313, 343],
      //   "waitTimes": [12, 7],
      //   "type": "food"
      // },
      // {
      //   "name": "The Carvery",
      //   "logoPath": "mlb/braves/concession_logos/carvery.png",
      //   "sections": [111],
      //   "waitTimes": [11],
      //   "type": "food"
      // },
      // {
      //   "name": "Center Field Market",
      //   "logoPath": "mlb/braves/concession_logos/centerfield.png",
      //   "sections": [149],
      //   "waitTimes": [17],
      //   "type": "food"
      // },
      // {
      //   "name": "Chick-Fil-A",
      //   "logoPath": "mlb/braves/concession_logos/cfa.png",
      //   "sections": [143, 106, 233, 326],
      //   "waitTimes": [35, 25, 10, 3],
      //   "type": "food"
      // },
      // {
      //   "name": "Conecuh",
      //   "logoPath": "mlb/braves/concession_logos/conecuh_sausage.png",
      //   "sections": [310],
      //   "waitTimes": [5],
      //   "type": "food"
      // },
      // {
      //   "name": "Coops Champion Chicken",
      //   "logoPath": "mlb/braves/concession_logos/coops_champion_chicken.png",
      //   "sections": [138, 320],
      //   "waitTimes": [2, 12],
      //   "type": "food"
      // },
      // {
      //   "name": "Coors Light Chophouse",
      //   "logoPath": "mlb/braves/concession_logos/coors_light_chophouse.png",
      //   "sections": [158],
      //   "waitTimes": [6],
      //   "type": "food"
      // },
      // {
      //   "name": "Dippin' Dots",
      //   "logoPath": "mlb/braves/concession_logos/dippin_dots.png",
      //   "sections": [142, 156, 220, 230, 340],
      //   "waitTimes": [5, 6, 4, 7, 5],
      //   "type": "food"
      // },
      // {
      //   "name": "Fox Brothers BBQ",
      //   "logoPath": "mlb/braves/concession_logos/fox_bros_bbq.png",
      //   "sections": [151],
      //   "waitTimes": [3],
      //   "type": "food"
      // },
      // {
      //   "name": "Freds Meat & Bread",
      //   "logoPath": "mlb/braves/concession_logos/freds_meat_bread.png",
      //   "sections": [310],
      //   "waitTimes": [7],
      //   "type": "food"
      // },
      // {
      //   "name": "Heaps",
      //   "logoPath": "mlb/braves/concession_logos/heaps.png",
      //   "sections": [310],
      //   "waitTimes": [10],
      //   "type": "food"
      // },
      // {
      //   "name": "H&F Burgers",
      //   "logoPath": "mlb/braves/concession_logos/hf_burgers.png",
      //   "sections": [155, 315, 335],
      //   "waitTimes": [9, 2, 15],
      //   "type": "food"
      // },
      // {
      //   "name": "Ole Mole Nachos",
      //   "logoPath": "mlb/braves/concession_logos/ole_mole_nachos.png",
      //   "sections": [146],
      //   "waitTimes": [11],
      //   "type": "food"
      // },
      // {
      //   "name": "Mayfield Ice Cream Stand",
      //   "logoPath": "mlb/braves/concession_logos/mayfield_dairy_farms.png",
      //   "sections": [148],
      //   "waitTimes": [2],
      //   "type": "food"
      // },
      // {
      //   "name": "Papi's Cuban Grill",
      //   "logoPath": "mlb/braves/concession_logos/papis_cuban_grill.png",
      //   "sections": [106],
      //   "waitTimes": [13],
      //   "type": "food"
      // },
      // {
      //   "name": "Sandlot Snacks",
      //   "logoPath": "mlb/braves/concession_logos/sandlot_snacks.png",
      //   "sections": [155],
      //   "waitTimes": [3],
      //   "type": "food"
      // },
      // {
      //   "name": "The Sip",
      //   "logoPath": "mlb/braves/concession_logos/the_sip.png",
      //   "sections": [344],
      //   "waitTimes": [7],
      //   "type": "food"
      // },
      // {
      //   "name": "The Slice",
      //   "logoPath": "mlb/braves/concession_logos/the_slice.png",
      //   "sections": [112, 150, 215, 239],
      //   "waitTimes": [9, 8, 10, 6],
      //   "type": "food"
      // },
      // {
      //   "name": "Slutty Vegan",
      //   "logoPath": "mlb/braves/concession_logos/slutty_vegan.png",
      //   "sections": [145],
      //   "waitTimes": [12],
      //   "type": "food"
      // },
      // {
      //   "name": "Smokey Q",
      //   "logoPath": "mlb/braves/concession_logos/smokey_q.png",
      //   "sections": [116, 329],
      //   "waitTimes": [11, 9],
      //   "type": "food"
      // },
      // {
      //   "name": "Taco Faco Factory",
      //   "logoPath": "mlb/braves/concession_logos/taco_factory.png",
      //   "sections": [152],
      //   "waitTimes": [10],
      //   "type": "food"
      // },
      // {
      //   "name": "ATL Alehouse",
      //   "logoPath": "mlb/braves/concession_logos/atl_alehouse.png",
      //   "sections": [331],
      //   "waitTimes": [5],
      //   "type": "beverage"
      // },
      // {
      //   "name": "Crown Royal Walk Off Market",
      //   "logoPath": "mlb/braves/concession_logos/crown_royal_market.png",
      //   "sections": [130],
      //   "waitTimes": [6],
      //   "type": "beverage"
      // },
      // {
      //   "name": "Cutwater Stand",
      //   "logoPath": "mlb/braves/concession_logos/cutwater_spirits.png",
      //   "sections": [120, 152],
      //   "waitTimes": [6, 12],
      //   "type": "beverage"
      // },
      // {
      //   "name": "Surfside Sandbar",
      //   "logoPath": "mlb/braves/concession_logos/surfside_sandbar.png",
      //   "sections": [320],
      //   "waitTimes": [3],
      //   "type": "beverage"
      // },
      // {
      //   "name": "Terrapin Beer Stand",
      //   "logoPath": "mlb/braves/concession_logos/terrapin_beer_stand.png",
      //   "sections": [322, 330],
      //   "waitTimes": [30, 12],
      //   "type": "beverage"
      // },
      // {
      //   "name": "Vizzy Seltzer Stand",
      //   "logoPath": "mlb/braves/concession_logos/terrapin_beer_stand.png",
      //   "sections": [216, 237, 243, 323, 329],
      //   "waitTimes": [6, 5, 4, 7, 7],
      //   "type": "beverage"
      // }
    ]
  };

  return {
    retail: [...authLocationData.retail, ...additionalData.retail],
    concessions: [...authLocationData.concessions, ...additionalData.concessions]
  };
};

// =============================================================================
// DUMMY OCCUPANCY DATA GENERATOR
// Add this entire block to your utils.js file
// =============================================================================

// Toggle this to enable/disable dummy data generation
export const USE_DUMMY_OCCUPANCY_DATA = false;

/**
 * Generate dummy occupancy data matching the controller's data structure
 * 
 * @param {Object} currentLocation - Location config with zones, floors, maxOccupancy
 * @param {Date} selectedDate - The date to generate data for
 * @param {Object} options - Optional configuration
 * @returns {Object} { processed: [], zones: [] }
 */
export const generateDummyOccupancyData = (currentLocation, selectedDate, options = {}) => {
  const {
    startMinute = 600,      // 10:00 AM
    endMinute = 1620,       // 3:00 AM next day (1440 + 180)
    peakMinute = 780,       // ~1:00 PM default peak
    peakSpread = 300,       // How spread out the peak is
    baseOccupancyPercent = 0.15,  // Base occupancy as % of max
    peakOccupancyPercent = 0.85,  // Peak occupancy as % of max
    noisePercent = 0.20,    // Random noise as % of max
    breachChance = 0.75,    // Chance of breaching capacity during peak (5%)
    breachMultiplier = 1.55 // How much to exceed capacity when breaching
  } = options;

  const zones = currentLocation.zones || [];
  const floors = currentLocation.floors || [];
  const maxOccupancy = currentLocation.maxOccupancy || 280;
  
  // Create base date from selectedDate
  const baseDate = new Date(selectedDate);
  baseDate.setHours(0, 0, 0, 0);

  // Generate zone-level data first
  const zoneDataMap = new Map();
  
  zones.forEach(zone => {
    zoneDataMap.set(zone.id, []);
  });

  // Process each minute
  const processed = [];
  
  for (let minute = 0; minute <= endMinute; minute++) {
    // Calculate time2 (ISO timestamp)
    let time2Date;
    if (minute >= 1440) {
      // After midnight - next day
      time2Date = new Date(baseDate.getTime() + (minute * 60000));
    } else {
      time2Date = new Date(baseDate.getTime() + (minute * 60000));
    }
    const time2 = time2Date.toISOString();

    // Calculate occupancy factor based on time of day
    let occupancyFactor = 0;
    
    if (minute >= startMinute) {
      // Distance from peak (accounting for day wrap)
      const distanceFromPeak = Math.abs(minute - peakMinute);
      
      // Bell curve-ish shape
      const peakFactor = Math.max(0, 1 - (distanceFromPeak / peakSpread));
      const smoothPeakFactor = Math.pow(peakFactor, 1.5); // Smoother curve
      
      occupancyFactor = baseOccupancyPercent + (peakOccupancyPercent - baseOccupancyPercent) * smoothPeakFactor;
      
      // Add time-based patterns (slight dip after peak, ramp down in evening)
      if (minute > peakMinute + 60 && minute < peakMinute + 180) {
        // Post-peak plateau
        occupancyFactor *= 0.9;
      } else if (minute > 1200 && minute < 1440) {
        // Evening decline (8 PM - midnight)
        const eveningFactor = 1 - ((minute - 1200) / 240) * 0.5;
        occupancyFactor *= eveningFactor;
      } else if (minute >= 1440) {
        // After midnight - rapid decline
        const lateNightFactor = Math.max(0.1, 1 - ((minute - 1440) / 180));
        occupancyFactor *= lateNightFactor * 0.3;
      }
    }

    // Generate traffic for each zone
    const traffic = [];
    let total = 0;

    zones.forEach(zone => {
      // Find which floor this zone belongs to
      const floor = floors.find(f => f.zoneSensorIds?.includes(zone.id));
      const floorMaxOccupancy = floor?.maxOccupancy || maxOccupancy;
      const zonesOnFloor = floor?.zoneSensorIds?.length || zones.length;
      
      // Calculate zone's share of floor capacity
      const zoneMaxOccupancy = floorMaxOccupancy / zonesOnFloor;
      
      // Floor-specific multiplier (e.g., floor 1 busier than floor 2)
      const floorMultiplier = floor?.id === 1 ? 1.1 : floor?.id === 2 ? 0.85 : 1.0;
      
      // Calculate base zone occupancy
      let zoneOccupancy = zoneMaxOccupancy * occupancyFactor * floorMultiplier;
      
      // Add noise
      const noise = (Math.random() - 0.5) * 1.5 * zoneMaxOccupancy * noisePercent;
      zoneOccupancy += noise;
      
      // Occasionally simulate breaches during peak hours
      const isPeakHours = minute > peakMinute - 60 && minute < peakMinute + 120;
      if (isPeakHours && Math.random() < breachChance) {
        zoneOccupancy = zoneMaxOccupancy * breachMultiplier * (1 + Math.random() * 0.1);
      }
      
      // Ensure non-negative and round
      zoneOccupancy = Math.max(0, Math.round(zoneOccupancy));
      
      traffic.push({
        id: zone.id,
        traffic: zoneOccupancy
      });
      
      total += zoneOccupancy;
      
      // Store in zone data map for zones array
      const zoneData = zoneDataMap.get(zone.id);
      zoneData.push({
        zone: zone.id,
        time: minute,
        time2: time2,
        traffic: zoneOccupancy
      });
    });

    // Calculate percentage
    const perc = Math.round((total / maxOccupancy) * 100);

    processed.push({
      time: minute,
      time2,
      total,
      perc,
      traffic
    });
  }

  // Build zones array (same structure as controller produces)
  const zonesArray = zones.map(zone => {
    const zoneData = zoneDataMap.get(zone.id) || [];
    
    const peak = zoneData.reduce(
      (max, item) => (item.traffic > max.traffic ? item : max),
      { traffic: -Infinity }
    );

    return {
      zoneId: zone.id,
      zoneData,
      label: zone.label,
      x: zone.x,
      y: zone.y,
      redDotX: zone.circleX,
      redDotY: zone.circleY,
      peak
    };
  });

  return {
    processed,
    zones: zonesArray
  };
};

/**
 * Helper to generate game-day specific dummy data with realistic patterns
 * 
 * @param {Object} currentLocation - Location config
 * @param {Object} currentGame - Current game with gatesOpen, gameStartTime
 * @param {Date} selectedDate - The date to generate data for
 * @returns {Object} { processed: [], zones: [] }
 */
export const generateGameDayDummyData = (currentLocation, currentGame, selectedDate) => {
  if (!currentGame) {
    return generateDummyOccupancyData(currentLocation, selectedDate);
  }

  // Parse game times
  const gatesOpenTime = currentGame.gatesOpen || "11:00";
  const gameStartTime = currentGame.gameStartTime || "13:00";
  
  const [gatesHours, gatesMinutes] = gatesOpenTime.split(':').map(Number);
  const [gameHours, gameMinutes] = gameStartTime.split(':').map(Number);
  
  const gatesOpenMinute = gatesHours * 60 + gatesMinutes;
  const gameStartMinute = gameHours * 60 + gameMinutes;

  return generateDummyOccupancyData(currentLocation, selectedDate, {
    startMinute: gatesOpenMinute - 60,  // Activity starts 1 hour before gates
    peakMinute: gameStartMinute + 30,   // Peak ~30 mins after game start
    peakSpread: 180,                     // Spread across 3 hours
    baseOccupancyPercent: 0.20,         // Higher base for game days
    peakOccupancyPercent: 0.90,         // Higher peak for game days
    breachChance: 0.08,                  // Slightly higher breach chance
  });
};

// =============================================================================
// END DUMMY OCCUPANCY DATA GENERATOR
// =============================================================================